import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location, PlatformLocation } from '@angular/common';
import { Client } from '../client';
import { User } from '../user';
import { Comment } from '../comment';
import { CommentType } from '../commentType';
import { ClientsService } from '../clients.service';
import { UsersService } from '../users.service';
import { CommentsService } from '../comments.service';
import { getObjIndex, formatDate, formatGetTime } from '../functions';
import { UserAuthService } from '../user-auth.service';
import { delay } from '../functions';


@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.css'],
  providers: [
	ClientsService,
	UsersService,
	CommentsService,
  ],
})


export class ClientsComponent implements OnInit {
	title = 'Bedriftsoversikt';
	clients: Client[] = [];
	selectedClient: Client;
	selectedClientID: number;
	tertiaryItem: {
		type: string;
		data: any;
	};
	action: string;
	params: {};
	searchQuery: string;
	currentPage: number = 1;
	clientsRemaining: boolean = true;
	breadcrumbs = [ // ready the breadcrumbs
		{ // starting point
			name: this.title,
			deletes: ['selectedClient', 'tertiaryItem'],
		},
		{ // selected customer
			name: '',
			deletes: ['tertiaryItem'],
		},
		{ // selected tertiaryItem
			name: '',
		},
	];
	currentUserID: number;

	private user: UserAuthService;

	constructor( // make services and external classes available to our class
		private clientsService: ClientsService,
		private usersService: UsersService,
		private commentsService: CommentsService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private location: Location,
		private userAuthService: UserAuthService,
		private platformLocation: PlatformLocation) {
	}

	/**
	 * get clients
	 * @param boolean. clear. whether to delete current clients before adding the newly retrieved ones. optional. default = false
	 */
	getClients(clear: boolean = false, currentUserID: number = 0) {

		// when clearing current clients, reset to page 1
		if ( clear ) {
			this.currentPage = 1;
		}

		if ( this.action == "mine" ) {
			currentUserID = this.currentUserID;
		} else {
			currentUserID = 0;
		}

		let thisClass = this;
		
		delay(function() { // not the angular way of doing this, but it works well enough (for now)

			thisClass.clientsService.getClients(thisClass.currentPage, thisClass.searchQuery, currentUserID)
				// .subscribe((clients: Client[]) => { // throws compile error. not sure why
				.subscribe((clients: any) => { // works

					// separate different user roles of the clients' users
					for ( var i = 0; i < clients.length; ++i )
					{
						let client = clients[i];
						client.advisors = [];
						client.project_managers = [];

						for ( var j = 0; j < client.users.length; ++j )
						{
							let user = client.users[j];

							if ( user.userRoles.indexOf('advisor') !== -1 )
							{
								client.advisors.push( user );
							}
							if ( user.userRoles.indexOf('project_manager') !== -1 )
							{
								client.project_managers.push( user );
							}

						}
					}

					// if id of last item of newly retrieved clients is similar to id of last item in clients already in this.clients, 
					// that means there's no more data to load, so we return
					if (thisClass.currentPage > 1 &&
						thisClass.clients[thisClass.clients.length-1]['id'] == clients[clients.length-1]['id'] ) {
						thisClass.clientsRemaining = false;
						return;
					}

					if ( clear ) {
						thisClass.clients = clients;
						thisClass.clientsRemaining = true;
						return;
					}

					let combinedClients = thisClass.clients.concat(clients); // works for both first and subsequent requests
					thisClass.clients = combinedClients;

				});

		}, 400);
	}

	/**
	 * select client
	 * @param object/number, required - client. if object (usually as a result of user interaction, e.g. click on some link), assign directly and route accordingly. if number (usually through url), fetch client then assign.
	 */
	selectClient( client: any ) {

		if ( typeof client == 'object' ) {

			this.selectedClient = client;
			this.router.navigate([ 'clients' + '/' + this.action, {id: this.selectedClient['id']} ]);
			this.modifySelectedClient();

		} else if ( !isNaN(client) ) { // if number

			this.selectedClient = this.clients[getObjIndex(this.selectedClientID, this.clients)];

			if ( this.selectedClient == undefined ) {
				this.clientsService.getClient( client )
					.subscribe( 
						(client: Client) => {
							this.selectedClient = client;
							this.modifySelectedClient();

							let tertiaryItemType = this.params['expand'];
							if ( this.selectedClient && tertiaryItemType == 'comments' ) {
								this.selectTertiaryItem('comments', this.selectedClient['comments']);
							}

						}
					);
			} else {
				this.modifySelectedClient();
			}

		}

	}

	/**
	 * create client
	 * @param string, required - name
	 * @param string, required - orgNr
	 * @param string, optional (default: null) - invoiceEmail
	 * @param number/boolean (0/1), optional (default: 0) - invoiceByEmail
	 * @param string, optional (default: null) - nextFollowUpDate
	 *
	 * @return client object
	 *
	 * note to self: maybe better to pass the object itself as parameter?
	 *
	 */
	createClient( name: string, orgNr: string, invoiceEmail: string = null, invoiceByEmail: number = 0, 
								url: string = null, email: string = null, phone: string = null, 
								nextFollowUpDate: string = null ) {
	// createClient( clientObj: {} ) {
		let clientObj = {
			'name': name,
			'org_nr': orgNr,
			'email_invoice': invoiceByEmail,
			'invoice_email': invoiceEmail,
			'next_follow_up': nextFollowUpDate,
			'url': url,
			'email': email,
			'phone': phone,
		}

		this.clientsService.createClient( clientObj )
			.subscribe((client: Client) => {
				this.clients.push( client );
			});
	}

	modifySelectedClient() {

		if ( this.selectedClient != undefined ) {

			this.modifyBreadcrumbs(1, ['name'], [this.selectedClient['name']]);

			// get username of 'created by'
			if ( this.selectedClient['created_by'] ) {
				this.getUser( this.selectedClient['created_by'] ).then(
					(data: any) => {
						this.selectedClient.created_by_username = data.username;
					});
			}

			// get username of 'updated by'
			if ( this.selectedClient['updated_by'] ) {
				this.getUser( this.selectedClient['updated_by'] ).then(
					(data: any) => {
						this.selectedClient.updated_by_username = data.username;
					});
			}

			// date format updated at
			if ( this.selectedClient['updated_at'] ) {
				this.selectedClient['updated_at_formatted'] = formatDate(this.selectedClient['updated_at']);
			}

			// date format created at
			if ( this.selectedClient['created_at'] ) {
				this.selectedClient['created_at_formatted'] = formatDate(this.selectedClient['created_at']);
			}

			// get usernames of comments
			this.getCommentUsername();
			this.getCommentType();
			this.formatCommentDate();

		}

	}

	deleteClient( id: number ) {
		this.clientsService.deleteClient( id )
			.subscribe(response => {
				this.clients.splice( getObjIndex(id, this.clients), 1 ); // update the view
				delete this.selectedClient;
				this.router.navigate([ 'clients' + '/' + this.action ]);
			});
	}


	/**
	 * search for client
	 * @param string. query. searches in client names
	 */
	searchClients(searchQuery: string) {
		this.clientsService.searchClients(searchQuery)
			.subscribe(
				(response: Client[]) => {
					// console.log(response);
				}
			);
	}

	/**
	 * get username of comment
	 * @param number. comment index (in this.selectedClient.comments). optional. 
	 * if not passed, will get usernames of all comments (in this.selectedClient.comments).
	 */
	getCommentUsername(index: number = null) {

		if (index) {

			this.getUser( this.selectedClient.comments[index].created_by ).then(
					(data: any) => {
						this.selectedClient.comments[index]['created_by_username'] = data.username;
					});

		} else {

			if ( this.selectedClient.comments ) {

				for (var i = 0; i < this.selectedClient.comments.length; ++i) {
					let j = i;
					this.getUser( this.selectedClient.comments[i].created_by ).then(
						(data: any) => {
							if ( this.selectedClient && this.selectedClient.comments && this.selectedClient.comments[j] ) {
								this.selectedClient.comments[j]['created_by_username'] = data.username;
							}
						});
				}

			}

		}

	}

	/**
	 * get username of comment
	 * @param number. comment index (in this.selectedClient.comments).
	 * if not passed, will get usernames of all comments (in this.selectedClient.comments).
	 */
	getCommentType(index: number = null) {

		if (index) {

			this.getType( this.selectedClient.comments[index].type ).then( 
				(data: any) => {
					this.selectedClient.comments[index]['type_name'] = data.name;
				}
			);

		} else {

			if ( this.selectedClient.comments ) {

				for (var i = 0; i < this.selectedClient.comments.length; ++i) {
					let j = i;
					if (this.selectedClient.comments[i].type) {
						this.getType( this.selectedClient.comments[i].type ).then(
							(data: any) => {
								if ( this.selectedClient && this.selectedClient.comments && this.selectedClient.comments[j] ) {
									this.selectedClient.comments[j]['type_name'] = data.name;
								}
							});
					} else {
						this.selectedClient.comments[j]['type_name'] = "KOMMENTAR";
					}
				}

			}

		}

	}

	/**
	 * format date of comment
	 * @param number. comment index (in this.selectedClient.comments). optional. 
	 * if not passed, will format dates of all comments (in this.selectedClient.comments).
	 */
	formatCommentDate(index: number = null) {

		if (index) {

			let comment = this.selectedClient.comments[index];
			comment['created_at_formatted'] = formatDate( comment['created_at'] );
			comment['created_at_time'] = formatGetTime( comment['created_at'] );
			if ( comment['updated_at'] ) {
				comment['updated_at_formatted'] = formatDate( comment['updated_at'] );
				comment['updated_at_time'] = formatGetTime( comment['updated_at'] );
			}

		} else {

			if ( this.selectedClient.comments ) {

				for (var i = 0; i < this.selectedClient.comments.length; ++i) {
					let comment = this.selectedClient.comments[i];
					comment['created_at_formatted'] = formatDate( comment['created_at'] );
					comment['created_at_time'] = formatGetTime( comment['created_at'] );
					if ( comment['updated_at'] ) {
						comment['updated_at_formatted'] = formatDate( comment['updated_at'] );
						comment['updated_at_time'] = formatGetTime( comment['updated_at'] );
					}
				}

			}

		}
		
	}

	/**
	 * append a comment to the end of the selectedClient.comments array
	 * @param Comment
	 */
	prependComment( comment: Comment ) {
		this.selectedClient.comments.unshift(comment);
		this.getCommentUsername( 0 );
		this.getCommentType( 0 );
		this.formatCommentDate( 0 );
	}

	/**
	 * select item to display in tertiary pane
	 * @param string. type of data/information, used in html template to decide how to display. supported values: 'comments'
	 * @param any. the actual data/information
	 */
	selectTertiaryItem( type: string, data: any ) {

		this.tertiaryItem = {
			type: type,
			data: data,
		};

		if ( this.selectedClient && this.tertiaryItem ) {
			this.router.navigate([ 'clients' + '/' + this.action, {id: this.selectedClient['id'], expand: this.tertiaryItem['type']} ]);
			this.modifyBreadcrumbs(2, ['name'], [this.tertiaryItem['type']]);
		}

	}

	/**
	 * unlike most *getSomething* methods, this returns a promise
	 */
	getUser( id: number ) {

		if ( id ) {

			return this.usersService.getUser(id)
								.toPromise();
								
		}

	}

	/**
	 * unlike most *getSomething* methods, this returns a promise
	 */
	getType( id: number ) {

		if ( id ) {

			return this.commentsService.getType(id)
								.toPromise();
								
		}

	}

	/**
	 * goBack to another pane
	 * @param HTMLElement, required - eventTarget. the element that triggered the method call. used to determine wheter secondary or tertiary pane was closed
	 */
	goBack(eventTarget) {

		// get classes of element that triggered method call
		let classList = eventTarget.classList;
		classList = Object.keys( classList ).map( key => classList[key] ); // object to array conversion

		// let closestPaneClassList = this.functions.findParent( 'swPane', eventTarget ).classList;
		// closestPaneClassList = Object.keys( closestPaneClassList ).map( key => closestPaneClassList[key] ); // object to array conversion
		// let clickingPrimaryPane = closestPaneClassList.indexOf('swPane--primary');

		// did the element that triggered the method call have any of these classes?
		let clickingPrimaryPane = classList.indexOf('overlay'); // overlay is always in the primary pane (never in secondary or tertiary)
		let closingSecondaryPane = classList.indexOf('closeSecondaryPane');
		let closingTertiaryPane = classList.indexOf('closeTertiaryPane');

		// do stuff depending on what class the triggering element have
		if ( closingTertiaryPane != -1 ) {
			delete this.tertiaryItem;
			this.modifyBreadcrumbs(2, ['name'], ['']);
			this.router.navigate([ 'clients' + '/' + this.action, {id: this.selectedClient['id']} ]);
		} else if ( closingSecondaryPane != -1 ) {
			delete this.selectedClient;
			delete this.tertiaryItem;
			this.modifyBreadcrumbs(1, ['name'], ['']);
			this.modifyBreadcrumbs(2, ['name'], ['']);
			this.router.navigate([ 'clients' + '/' + this.action ]);
		} else if ( clickingPrimaryPane != -1 && this.selectedClient && this.tertiaryItem ) {
			delete this.selectedClient;
			delete this.tertiaryItem;
			this.modifyBreadcrumbs(1, ['name'], ['']);
			this.modifyBreadcrumbs(2, ['name'], ['']);
			this.router.navigate([ 'clients' + '/' + this.action ]);
		}
	}

	/**
	 * add/clear properties and values from breadcrumbs array (when various events occur)
	 * @param number, required - index. which item in the breadcrumbs array (0-based)
	 * @param array, optional - properties. which properties to change
	 * @param array, optional - values. which values to change to. order must correspond to order of properties param
	 */
	modifyBreadcrumbs( index: number, properties = [], values = [] ) {
		for ( var i = 0; i < properties.length; ++i ) {
			this.breadcrumbs[index][properties[i]] = values[i];
		}
	}

	/**
	 * handle breadcrumb interaction
	 * @param number, required - breadcrumbIndex. which item in the breadcrumbs array (0-based) that was interacted with
	 * @param array, optional - deleteMe. which properties to delete
	 */
	selectBreadcrumb( breadcrumbIndex: number, deleteMe = [] ) {
		// delete breadcrumbs
		for (var i = breadcrumbIndex + 1; i < this.breadcrumbs.length ; ++i) {
			this.modifyBreadcrumbs(i, ['name'], ['']);
		}
		// delete class properties
		for ( var i = 0; i < deleteMe.length; ++i ) {
			delete this[deleteMe[i]];
		}

		// change urls accordingly
		if ( this.selectedClient && this.tertiaryItem ) {
			this.router.navigate([ 'clients' + '/' + this.action, {id: this.selectedClient['id'], expand: this.tertiaryItem['type']} ]);
		} else if ( this.selectedClient ) {
			this.router.navigate([ 'clients' + '/' + this.action, {id: this.selectedClient['id']} ]);
		} else {
			this.router.navigate([ 'clients' + '/' + this.action ]);
		}
	}

	goToClientDetail(id: number) {
		this.router.navigate([ 'client' + '/' + id ]);
	}

	onPopState() { // ensure popState functionality (i.e. browser back/forward buttons)
		this.activatedRoute.params // subscribe to route params (so we have access to them)
			.subscribe( (params: {}) => {
				this.params = params;
				this.action = this.params['action'];

				this.selectedClientID = +this.params['id']; // + in front converts string to integer (hack-ish, but works well)
				let tertiaryItemType = this.params['expand'];

				if ( this.action == "mine" ) {
					this.getClients( true, this.currentUserID );
				} else {
					this.getClients( true );
				}

				// ensures the selected client and tertiaryItem are deleted when clearing the id and expand params from url (helpful e.g. when navigating using the site menu):
				if ( !this.selectedClientID ) {
					delete this.selectedClient;
					this.modifyBreadcrumbs(1, ['name'], ['']);
				}
				if ( !tertiaryItemType ) {
					delete this.tertiaryItem;
					this.modifyBreadcrumbs(2, ['name'], ['']);
				}

			});

		this.platformLocation.onPopState( () => { // the actual popState handling

			// store a reference to this class and wait 1ms before updating values. this ensures we get the url values from AFTER the popState is done, not BEFORE it triggers
			var thisClass = this;
			setTimeout(function() { // might an rxjs/angular alternative to setTimeout, but haven't found it yet. this works well enough though
				thisClass.selectedClientID = +thisClass.params['id']; // + in front converts string to integer (hack-ish, but works well)
				// thisClass.selectedClient = thisClass.clients[getObjIndex(thisClass.selectedClientID, thisClass.clients)];

				thisClass.selectClient(thisClass.selectedClientID);
				let tertiaryItemType = thisClass.params['expand'];
				if ( tertiaryItemType == 'comments' ) {
					thisClass.selectTertiaryItem(tertiaryItemType, thisClass.selectedClient['comments']);
				}

				// update breadcrumbs
				if ( thisClass.selectedClient ) {
					thisClass.modifyBreadcrumbs(1, ['name'], [thisClass.selectedClient['name']]);
				} else {
					thisClass.modifyBreadcrumbs(1, ['name'], ['']);
				}
				if ( thisClass.tertiaryItem ) {
					thisClass.modifyBreadcrumbs(2, ['name'], [thisClass.tertiaryItem['type']]);
				} else {
					thisClass.modifyBreadcrumbs(2, ['name'], ['']);
				}
			}, 1);

    	} );

	}

	ngOnInit() {

		if ( this.userAuthService.getCurrentUser() ) {
			this.currentUserID = this.userAuthService.getCurrentUser()['user_id'];
		}

		this.onPopState();

		// Let's display selected client on load if URL tells us so
		this.selectClient( this.selectedClientID );

	}

}


// WEBPACK FOOTER //
// ./src/app/clients/clients.component.ts